<script setup lang="ts">
import { ref } from 'vue'
import DataTable, { type DataTableColumn } from '@uiKit/elements/DataTable.vue'
import Button from '@uiKit/elements/Button.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Chip from '@uiKit/elements/Chip.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import Popover from '@uiKit/elements/Popover.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import { formatDate } from '@shared/utils/dateTimeUtils'
import { formatFeeCalculation, formatVatRate } from '@shared/utils/feeUtils'
import {
  FEE_LIFECYCLE_STAGE_LABEL,
  FEE_PARTY_ROLE_LABEL,
  FEE_RECURRENCE_LABEL,
} from '@shared/constants/fees'
import type { FeeDefinition } from '../types/feeTypes'

defineProps<{
  fees: FeeDefinition[]
  isLoading: boolean
}>()

const emit = defineEmits<{
  (e: 'viewDetails', fee: FeeDefinition): void
  (e: 'viewHistory', fee: FeeDefinition): void
}>()

// The table slots hand back an untyped row, so every label lookup goes through
// a typed reader rather than indexing a label map with `any`.
const stageLabel = (fee: FeeDefinition): string => FEE_LIFECYCLE_STAGE_LABEL[fee.lifecycleStage]
const paidByLabel = (fee: FeeDefinition): string => FEE_PARTY_ROLE_LABEL[fee.paidByRole]
const paidToLabel = (fee: FeeDefinition): string => FEE_PARTY_ROLE_LABEL[fee.paidToRole]
const recurrenceLabel = (fee: FeeDefinition): string => FEE_RECURRENCE_LABEL[fee.recurrence]

function handleRowClick(event: { data: FeeDefinition }): void {
  emit('viewDetails', event.data)
}

interface FeeRowAction {
  label: string
  icon: PiIconName
  run: (fee: FeeDefinition) => void
}

const ROW_ACTIONS: FeeRowAction[] = [
  { label: 'View', icon: 'eye', run: (fee) => emit('viewDetails', fee) },
  { label: 'History', icon: 'history', run: (fee) => emit('viewHistory', fee) },
]

// One popover retargeted per row, not one per row — the same approach the kit's
// DataTableV2 kebab takes (the VAISP processing queue's row menu). The row the
// menu was opened from is held here because the popover renders outside the
// table's row scope.
const actionsPopover = ref<InstanceType<typeof Popover> | null>(null)
const activeFee = ref<FeeDefinition | null>(null)

function openActions(event: Event, fee: FeeDefinition): void {
  activeFee.value = fee
  actionsPopover.value?.show(event)
}

function runAction(action: FeeRowAction): void {
  const fee = activeFee.value
  if (!fee) return
  action.run(fee)
  actionsPopover.value?.hide()
}

// Sorting is off on every column. Several of these fields are composed in a
// slot (`value`, `vat`, `status`) and do not exist on the row, so the kit's
// default sortable affordance offered a header click that could never reorder
// anything. The list is already narrowed by the four filters above it, and the
// backend returns one page of in-force definitions.
const columns: DataTableColumn[] = [
  { field: 'name', header: 'FEE', minWidth: '220px', sortable: false },
  { field: 'lifecycleStage', header: 'STAGE', minWidth: '150px', sortable: false },
  { field: 'paidByRole', header: 'PAID BY', minWidth: '150px', sortable: false },
  { field: 'paidToRole', header: 'PAID TO', minWidth: '150px', sortable: false },
  { field: 'value', header: 'VALUE IN FORCE', minWidth: '200px', sortable: false },
  { field: 'recurrence', header: 'RECURRENCE', minWidth: '120px', sortable: false },
  { field: 'vat', header: 'VAT', minWidth: '110px', sortable: false },
  { field: 'validFrom', header: 'IN FORCE SINCE', minWidth: '150px', sortable: false },
  { field: 'status', header: 'STATUS', minWidth: '110px', align: 'center', sortable: false },
  { field: 'actions', header: '', width: '56px', align: 'right', sortable: false },
]
</script>

<template>
  <DataTable
    :value="fees"
    :is-loading="isLoading"
    :columns="columns"
    class="fee-table"
    :paginator="false"
    @row-click="handleRowClick"
  >
    <template #empty>
      <EmptyState
        icon="percentage"
        title="No fees defined"
        description="No fee definitions match this filter."
        size="compact"
      />
    </template>

    <template #name="{ data }">
      <Box flex direction="col">
        <Text type="body" weight="bold">{{ data.name }}</Text>
        <Text type="caption" class="fee-code">{{ data.code }}</Text>
      </Box>
    </template>

    <template #lifecycleStage="{ data }">
      <Text type="body">{{ stageLabel(data) }}</Text>
    </template>

    <template #paidByRole="{ data }">
      <Text type="body">{{ paidByLabel(data) }}</Text>
    </template>

    <template #paidToRole="{ data }">
      <Text type="body" color="muted">{{ paidToLabel(data) }}</Text>
    </template>

    <template #value="{ data }">
      <Box flex direction="col">
        <Text type="body" weight="bold" color="primary">
          {{
            formatFeeCalculation({
              value: data.defaultValue,
              valueType: data.valueType,
              valueBasis: data.valueBasis,
              unitBasis: data.unitBasis,
            })
          }}
        </Text>
        <Text v-if="!data.isCustomisable" type="caption" class="fee-locked">
          Fixed — properties cannot override
        </Text>
      </Box>
    </template>

    <template #recurrence="{ data }">
      <Text type="body" color="muted">{{ recurrenceLabel(data) }}</Text>
    </template>

    <template #vat="{ data }">
      <Text type="body" color="muted">
        {{ formatVatRate(data.isVatApplicable, data.vatPercentage) }}
      </Text>
    </template>

    <template #validFrom="{ data }">
      <Text type="body">{{ formatDate(data.validFrom) }}</Text>
    </template>

    <template #status="{ data }">
      <Chip
        :label="data.isActive ? 'ACTIVE' : 'DISABLED'"
        :color="data.isActive ? 'success' : 'warning'"
        variant="tonal"
        size="small"
        dot
      />
    </template>

    <template #actions="{ data }">
      <Button
        variant="text"
        color="secondary"
        size="small"
        icon="ellipsis-v"
        aria-label="Fee actions"
        class="fee-kebab"
        @click.stop="openActions($event, data)"
      />
    </template>
  </DataTable>

  <Popover ref="actionsPopover">
    <Box flex direction="col" class="fee-actions-menu">
      <button
        v-for="action in ROW_ACTIONS"
        :key="action.label"
        type="button"
        class="fee-actions-menu__item"
        @click="runAction(action)"
      >
        <PiIcon :name="action.icon" size="sm" />
        <Text type="body-sm">{{ action.label }}</Text>
      </button>
    </Box>
  </Popover>
</template>

<style scoped>
.fee-code {
  color: var(--p-text-muted-color);
  font-family: var(--font-mono, monospace);
}

.fee-locked {
  color: var(--p-text-muted-color);
  font-style: italic;
}

/* Kebab + menu item chrome copied from the kit's DataTableV2 so the row menu
   here is indistinguishable from every other queue's. */
.fee-kebab {
  width: 32px;
  height: 32px;
  padding: 0;
}

.fee-actions-menu {
  padding: 0.25rem;
  min-width: 120px;
}

.fee-actions-menu__item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3rem 0.5rem;
  width: 100%;
  background: transparent;
  border: none;
  border-radius: var(--p-border-radius-sm);
  color: var(--p-text-color);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.fee-actions-menu__item:hover {
  background: var(--p-surface-100);
}

:deep(.fee-table tr.p-datatable-row) {
  cursor: pointer;
}
</style>
